import { useMemo } from 'react';

type RGBA = [number, number, number, number];

export interface CodeColorBarProps {
  /** Map of 1-based line number → RGBA colour for every color() call in source */
  colorLineMap: Map<number, RGBA>;
  /** 1-based line number of the currently hovered parameter, or null */
  hoveredParamLine: number | null;
  /**
   * Returns the content-space top pixel for a given 1-based line number,
   * as reported by `editor.getTopForLineNumber(n)`.
   * This is relative to the scrollable content origin (excludes scroll offset).
   */
  getLineTop: (lineNumber: number) => number;
  /** Current vertical scroll offset of the editor in px */
  scrollTop: number;
  /** Monaco editor line height in px (used to center markers vertically) */
  lineHeight: number;
  /** Visible height of the editor container in px */
  containerHeight: number;
  /** Top padding configured on the editor (Monaco `padding.top`) */
  paddingTop?: number;
  /**
   * Incremented on every model content change. Forces the useMemo to
   * re-evaluate getLineTop() even when scroll/lineHeight are unchanged,
   * ensuring markers stay aligned after parameter edits shift line positions.
   */
  contentVersion?: number;
}

/**
 * A 4px-wide vertical strip rendered at the far-left edge of the code editor.
 * Each compiled color() call gets a coloured tick mark aligned to its source
 * line. When the user hovers a parameter in the sidebar, a white tick appears
 * at that parameter's declaration line.
 */
export function CodeColorBar({
  colorLineMap,
  hoveredParamLine,
  getLineTop,
  scrollTop,
  lineHeight,
  containerHeight,
  paddingTop = 12,
  contentVersion,
}: CodeColorBarProps) {
  const MARKER_H = 24;

  const markers = useMemo(() => {
    const items: { top: number; color: string; isParam: boolean }[] = [];

    const lineTopToVisual = (lineNum: number) =>
      // getTopForLineNumber is relative to content origin (line 1 top = 0).
      // Add paddingTop to convert to scroll-space, then subtract scrollTop
      // to convert to viewport-space.
      paddingTop + getLineTop(lineNum) + lineHeight / 2 - MARKER_H / 2 - scrollTop;

    // Color markers
    for (const [lineNum, rgba] of colorLineMap) {
      const top = lineTopToVisual(lineNum);
      // Skip markers completely outside the visible area
      if (top + MARKER_H < 0 || top > containerHeight) continue;
      const [r, g, b, a] = rgba;
      items.push({
        top,
        color: `rgba(${Math.round(r * 255)},${Math.round(g * 255)},${Math.round(b * 255)},${Math.max(0.6, a)})`,
        isParam: false,
      });
    }

    // Hovered parameter marker
    if (hoveredParamLine !== null) {
      const top = lineTopToVisual(hoveredParamLine);
      if (top + MARKER_H >= 0 && top <= containerHeight) {
        items.push({
          top,
          color: 'rgba(255,255,255,0.85)',
          isParam: true,
        });
      }
    }

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorLineMap, hoveredParamLine, getLineTop, scrollTop, lineHeight, containerHeight, paddingTop, contentVersion]);

  if (colorLineMap.size === 0 && hoveredParamLine === null) return null;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute left-0 top-0 z-10 overflow-hidden"
      style={{ width: 4, height: containerHeight }}
    >
      {markers.map((m, idx) => (
        <div
          key={idx}
          style={{
            position: 'absolute',
            left: 0,
            top: m.top - 12,
            width: 4,
            height: MARKER_H,
            borderRadius: 1,
            backgroundColor: m.color,
            boxShadow: m.isParam
              ? '0 0 4px rgba(255,255,255,0.6)'
              : `0 0 3px ${m.color}`,
          }}
        />
      ))}
    </div>
  );
}
